<template>
  <div class="dashboard-container">
    <div class="mb-2 page-title">
      <i class="bi bi-person-badge"></i> Cetak Kartu Peserta (ID Card)
    </div>

    <!-- Pilih acara -->
    <section class="ui-card kartu__blok">
      <div class="ui-card__body">
        <label class="ui-field">
          <span class="ui-label">Pilih Acara / Turnamen</span>
          <select v-model="tournamentId" class="ui-input" @change="pilihAcara">
            <option value="">Pilih Acara</option>
            <option v-for="t in turnamen" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
        </label>
      </div>
    </section>

    <template v-if="tournamentId">
      <!-- Langkah 1: Desain Kartu (Khusus Panitia) -->
      <section v-if="isPanitia" class="ui-card kartu__blok">
        <div class="ui-card__body">
          <h2 class="kartu__judul"><span class="kartu__nomor">1</span> Desain Latar Kartu</h2>

          <!-- Status Desain Jika Sudah Tersimpan -->
          <div v-if="desain.latar_url" class="desain-status-card mb-3">
            <div class="d-flex align-items-center gap-3">
              <div class="desain-thumb">
                <img :src="desain.latar_url" alt="Desain Kartu Aktif" />
              </div>
              <div class="flex-grow-1">
                <div class="d-flex align-items-center gap-1.5 fw-bold text-success">
                  <i class="bi bi-check-circle-fill"></i> Desain Latar Kartu Sudah Tersimpan
                </div>
                <div class="desain-meta text-muted small mt-1">
                  Resolusi: <strong>{{ desain.lebar_asli }} &times; {{ desain.tinggi_asli }} px</strong> &bull; Status: <strong>Aktif</strong>
                </div>
                <p class="desain-ket small text-secondary mb-0 mt-1">
                  Desain ini sudah tersimpan permanen untuk acara ini. Anda <strong>tidak perlu mengunggah ulang</strong> setiap kali membuka halaman. Latar otomatis dipakai untuk tata letak dan cetak.
                </p>
              </div>
            </div>

            <div class="desain-actions mt-3 pt-2.5 border-top d-flex flex-wrap align-items-center gap-2">
              <label class="ui-btn ui-btn--sm ui-btn--secondary" :class="{ 'disabled': mengunggah }">
                <i class="bi bi-arrow-repeat"></i> Ganti Desain Latar Baru
                <input
                  type="file"
                  accept="image/*"
                  class="visually-hidden"
                  :disabled="mengunggah"
                  @change="unggahLatar"
                />
              </label>

              <button
                type="button"
                class="ui-btn ui-btn--sm ui-btn--ghost"
                :disabled="menerapkanTemplate"
                @click="gunakanTemplateBawaan"
              >
                <span v-if="menerapkanTemplate" class="ui-spinner"></span>
                <i v-else class="bi bi-magic"></i> Kembalikan ke Template Bawaan
              </button>
            </div>
          </div>

          <!-- Jika Belum Ada Desain -->
          <div v-else>
            <p class="kartu__teks">
              Unggah berkas latar belakang kartu (JPG/PNG resolusi tinggi), atau gunakan template latar bawaan dari sistem.
            </p>

            <div class="d-flex flex-wrap gap-2 align-items-center mb-3">
              <label class="ui-btn ui-btn--primary" :class="{ 'disabled': mengunggah }">
                <i class="bi bi-upload"></i> Unggah Latar Kustom
                <input
                  type="file"
                  accept="image/*"
                  class="visually-hidden"
                  :disabled="mengunggah"
                  @change="unggahLatar"
                />
              </label>

              <button
                type="button"
                class="ui-btn ui-btn--ghost"
                :disabled="menerapkanTemplate"
                @click="gunakanTemplateBawaan"
              >
                <span v-if="menerapkanTemplate" class="ui-spinner"></span>
                <i v-else class="bi bi-magic"></i> Gunakan Template Standar ARSPO
              </button>
            </div>
          </div>

          <p v-if="mengunggah" class="kartu__teks mt-2">
            <span class="ui-spinner"></span> Mengunggah desain…
          </p>

          <p v-if="galatUnggah" class="kartu__galat mt-2">
            <i class="bi bi-exclamation-triangle-fill"></i>
            {{ galatUnggah }}
          </p>
        </div>
      </section>

      <!-- Langkah 2: Tata Letak & Posisi Teks (Khusus Panitia) -->
      <section v-if="isPanitia && desain.latar_url" class="ui-card kartu__blok">
        <div class="ui-card__body">
          <div class="d-flex flex-wrap justify-content-between align-items-center mb-2">
            <h2 class="kartu__judul mb-0"><span class="kartu__nomor">2</span> Atur Posisi & Tata Letak</h2>

            <button
              type="button"
              class="ui-btn ui-btn--sm ui-btn--ghost"
              @click="aturUlangPosisiStandar"
            >
              <i class="bi bi-arrow-counterclockwise"></i> Reset Posisi Standar
            </button>
          </div>

          <p class="kartu__teks">
            Seret kotak kolom pada kanvas untuk mengatur posisinya. Anda juga dapat memilih contoh data atlet riil untuk melihat kecocokan tampilan secara langsung.
          </p>

          <!-- Pratinjau Switcher & Tambah Kolom -->
          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <label class="ui-field">
                <span class="ui-label">Pratinjau dengan Data:</span>
                <select v-model="selectedPreviewIndex" class="ui-input" @change="gantiPratinjauAtlet">
                  <option value="-1">Contoh Standar (Budi Santoso)</option>
                  <option v-for="(atlet, idx) in daftarAtletPratinjau" :key="atlet.id" :value="idx">
                    {{ atlet.nama }} ({{ atlet.kontingen }})
                  </option>
                </select>
              </label>
            </div>

            <div class="col-md-6">
              <label class="ui-field">
                <span class="ui-label">Tambah Kolom Informasi:</span>
                <div class="d-flex gap-2">
                  <select v-model="kolomBaru" class="ui-input">
                    <option value="">Pilih Kolom</option>
                    <option
                      v-for="(label, kunci) in daftarPilihanKolom"
                      :key="kunci"
                      :value="kunci"
                      :disabled="sudahDipakai(kunci)"
                    >
                      {{ label }}{{ sudahDipakai(kunci) ? ' (sudah ada)' : '' }}
                    </option>
                  </select>
                  <button type="button" class="ui-btn ui-btn--secondary flex-shrink-0" :disabled="!kolomBaru" @click="tambahKolom">
                    <i class="bi bi-plus-lg"></i> Tambah
                  </button>
                </div>
              </label>
            </div>
          </div>

          <!-- Kanvas Perancang Interaktif -->
          <div
            ref="kanvas"
            class="kanvas"
            :style="{ aspectRatio: `${desain.lebar_asli || 1000} / ${desain.tinggi_asli || 1500}` }"
            @pointermove="geser"
            @pointerup="lepas"
            @pointerleave="lepas"
          >
            <img :src="desain.latar_url" alt="Desain kartu" class="kanvas__latar" draggable="false" />

            <div
              v-for="(k, i) in (Array.isArray(kolom) ? kolom : [])"
              :key="k.kunci"
              class="kolom"
              :class="{ 'kolom--aktif': aktif === i, 'kolom--foto': k.kunci === 'foto', 'kolom--qr': k.kunci === 'qr_code' }"
              :style="gayaKolom(k, false, contoh[k.kunci])"
              @pointerdown="mulaiGeser(i, $event)"
            >
              <template v-if="k.kunci === 'foto'">
                <img v-if="contoh.foto" :src="contoh.foto" alt="Foto" class="kolom__foto-img" draggable="false" />
                <div v-else class="kolom__foto-placeholder">
                  <i class="bi bi-person-fill"></i>
                  <span class="kolom__foto-label">FOTO</span>
                </div>
              </template>
              <template v-else-if="k.kunci === 'qr_code'">
                <img v-if="contohQrUrl" :src="contohQrUrl" alt="QR Code" class="kolom__qr-img" draggable="false" />
                <div v-else class="kolom__qr-placeholder">
                  <i class="bi bi-qr-code"></i>
                  <span class="kolom__qr-label">QR</span>
                </div>
              </template>
              <template v-else>{{ contoh[k.kunci] || `[${kolomTersedia[k.kunci] || k.kunci}]` }}</template>
            </div>
          </div>

          <!-- Pengaturan Kolom Terpilih -->
          <div v-if="aktif !== null && Array.isArray(kolom) && kolom[aktif]" class="kartu__setelan">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <strong class="kartu__setelan-judul mb-0">
                <i class="bi bi-sliders"></i> Pengaturan: {{ kolomTersedia[kolom[aktif].kunci] || kolom[aktif].kunci }}
              </strong>
              <button type="button" class="ui-btn ui-btn--sm ui-btn--danger" @click="hapusKolom(aktif)">
                <i class="bi bi-trash"></i> Hapus Kolom
              </button>
            </div>

            <div class="kartu__setelan-grid">
              <label>
                Ukuran:
                <input
                  type="range"
                  min="1"
                  :max="kolom[aktif].kunci === 'foto' ? 45 : (kolom[aktif].kunci === 'qr_code' ? 35 : 25)"
                  step="0.2"
                  v-model.number="kolom[aktif].ukuran"
                />
                <span>{{ kolom[aktif].ukuran }}%</span>
              </label>

              <!-- Posisi Geser X (Horisontal) -->
              <label>
                Posisi X (Kiri-Kanan):
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.5"
                  v-model.number="kolom[aktif].x"
                />
                <span>{{ kolom[aktif].x }}%</span>
              </label>

              <!-- Posisi Geser Y (Vertikal) -->
              <label>
                Posisi Y (Atas-Bawah):
                <input
                  type="range"
                  min="0"
                  max="100"
                  step="0.5"
                  v-model.number="kolom[aktif].y"
                />
                <span>{{ kolom[aktif].y }}%</span>
              </label>

              <!-- Perataan / Anchor -->
              <label>
                Perataan:
                <select v-model="kolom[aktif].rata" class="ui-input">
                  <option value="left">Kiri</option>
                  <option value="center">Tengah</option>
                  <option value="right">Kanan</option>
                </select>
              </label>

              <label v-if="!['foto', 'qr_code'].includes(kolom[aktif].kunci)">
                Lebar Maksimal:
                <input type="range" min="30" max="100" step="1" v-model.number="kolom[aktif].lebar" />
                <span>{{ kolom[aktif].lebar || 80 }}%</span>
              </label>

              <label v-if="!['foto', 'qr_code'].includes(kolom[aktif].kunci)">
                Warna:
                <input type="color" v-model="kolom[aktif].warna" />
              </label>

              <label v-if="!['foto', 'qr_code'].includes(kolom[aktif].kunci)">
                <input type="checkbox" v-model="kolom[aktif].tebal" /> <strong>Tebal</strong>
              </label>

              <!-- Pintasan Posisi Cepat -->
              <div class="posisi-pintasan-box mt-3 pt-2.5 border-top w-100">
                <span class="small fw-bold text-secondary mb-2 d-block">
                  <i class="bi bi-lightning-charge-fill text-warning me-1"></i>Pintasan Posisi Cepat:
                </span>
                <div class="d-flex flex-wrap gap-2 align-items-center">
                  <button
                    type="button"
                    class="ui-btn ui-btn--sm ui-btn--secondary posisi-pill"
                    @click="rataTengah(aktif)"
                  >
                    <i class="bi bi-align-center"></i> Rata Tengah (X: 50%)
                  </button>
                  <template v-if="['qr_code', 'foto'].includes(kolom[aktif].kunci)">
                    <button
                      type="button"
                      class="ui-btn ui-btn--sm ui-btn--ghost posisi-pill"
                      @click="setPosisi(aktif, 15, 86, 'center')"
                    >
                      ↙ Kiri Bawah
                    </button>
                    <button
                      type="button"
                      class="ui-btn ui-btn--sm ui-btn--ghost posisi-pill"
                      @click="setPosisi(aktif, 50, 86, 'center')"
                    >
                      ⬇ Tengah Bawah
                    </button>
                    <button
                      type="button"
                      class="ui-btn ui-btn--sm ui-btn--ghost posisi-pill"
                      @click="setPosisi(aktif, 85, 86, 'center')"
                    >
                      ↘ Kanan Bawah
                    </button>
                    <button
                      type="button"
                      class="ui-btn ui-btn--sm ui-btn--ghost posisi-pill"
                      @click="setPosisi(aktif, 85, 15, 'center')"
                    >
                      ↗ Kanan Atas
                    </button>
                    <button
                      type="button"
                      class="ui-btn ui-btn--sm ui-btn--ghost posisi-pill"
                      @click="setPosisi(aktif, 15, 15, 'center')"
                    >
                      ↖ Kiri Atas
                    </button>
                  </template>
                </div>
              </div>
            </div>
          </div>

          <div class="mt-3">
            <button
              type="button"
              class="ui-btn ui-btn--primary"
              :disabled="menyimpan"
              @click="simpanKolom"
            >
              <span v-if="menyimpan" class="ui-spinner"></span>
              <i v-else class="bi bi-save"></i> Simpan Tata Letak
            </button>
          </div>
        </div>
      </section>

      <!-- Langkah 3: Cetak Kartu Peserta -->
      <section v-if="desain.latar_url && Array.isArray(kolom) && kolom.length" class="ui-card kartu__blok">
        <div class="ui-card__body">
          <h2 class="kartu__judul"><span class="kartu__nomor">{{ isPanitia ? '3' : '1' }}</span> Cetak Kartu Peserta</h2>
          <p class="kartu__teks">
            Pilih format tata letak kertas dan filter kontingen, kemudian klik <strong>Siapkan & Cetak Kartu</strong> untuk mencetak.
          </p>

          <div class="row g-3" style="max-width: 820px">
            <div class="col-md-6">
              <label class="ui-field">
                <span class="ui-label">Format Tata Letak Kertas</span>
                <select v-model="modeCetak" class="ui-input">
                  <optgroup label="Ukuran ID Card Standar (pas mika ID card)">
                    <option value="idcard_a4_9">ID Card Standar 5,4 × 8,6 cm — 9 Kartu per Lembar A4</option>
                  </optgroup>
                  <optgroup label="Kertas A4 (Paling Umum & Hemat Rekomendasi)">
                    <option value="a4_4_kartu">Kertas A4 — 4 Kartu per Lembar (Grid 2×2 Standar Kalung/Laminating)</option>
                    <option value="a4_8_kartu">Kertas A4 — 8 Kartu per Lembar (Grid 2×4 Kompak / Sangat Hemat)</option>
                    <option value="a4_2_kartu">Kertas A4 — 2 Kartu per Lembar (Ukuran Besar)</option>
                  </optgroup>
                  <optgroup label="Kertas A5 (148 × 210 mm)">
                    <option value="a5_2_kartu">Kertas A5 — 2 Kartu per Lembar</option>
                    <option value="a5_1_kartu">Kertas A5 — 1 Kartu per Lembar (Besar Penuh)</option>
                  </optgroup>
                  <optgroup label="Kertas A6 (105 × 148 mm — Standar ID Card)">
                    <option value="a6_1_kartu">Kertas A6 — 1 Kartu Pas Ukuran Standar (105 × 148 mm)</option>
                  </optgroup>
                  <optgroup label="Kertas Seri B (B4 & B5)">
                    <option value="b4_8_kartu">Kertas B4 — 8 Kartu per Lembar (Grid 2×4)</option>
                    <option value="b5_4_kartu">Kertas B5 — 4 Kartu per Lembar (Grid 2×2)</option>
                  </optgroup>
                  <optgroup label="Kustom / Kertas Satuan">
                    <option value="1_per_halaman">1 Kartu per Lembar (Ukuran Kustom Bebas)</option>
                  </optgroup>
                </select>
                <small class="kartu__teks d-block mt-1" v-if="modeCetak === 'idcard_a4_9'">
                  Ukuran kartu KTP/ATM (5,4 &times; 8,56 cm), pas masuk mika ID card biasa. 9 kartu per lembar A4 dengan garis potong.
                  Cetak dengan skala <strong>100% / Actual size</strong>, jangan "Fit to page".
                </small>
                <small class="kartu__teks d-block mt-1" v-else-if="modeCetak === 'a4_4_kartu'">
                  4 kartu ukuran standar per lembar A4 disertai garis potong rapi (paling pas untuk plastik kalung ID & laminating).
                </small>
                <small class="kartu__teks d-block mt-1" v-else-if="modeCetak === 'a4_8_kartu'">
                  8 kartu per lembar A4 (grid 2 kolom &times; 4 baris), sangat hemat kertas untuk banyak peserta.
                </small>
                <small class="kartu__teks d-block mt-1" v-else-if="modeCetak === 'a4_2_kartu'">
                  2 kartu besar per lembar A4 disertai garis potong.
                </small>
                <small class="kartu__teks d-block mt-1" v-else-if="modeCetak === 'a5_2_kartu'">
                  2 kartu pas per lembar kertas A5.
                </small>
                <small class="kartu__teks d-block mt-1" v-else-if="modeCetak === 'a5_1_kartu'">
                  1 kartu ukuran penuh di kertas A5.
                </small>
                <small class="kartu__teks d-block mt-1" v-else-if="modeCetak === 'a6_1_kartu'">
                  1 kartu persis seukuran kertas A6 (105 &times; 148 mm — standar pas kantong ID card).
                </small>
                <small class="kartu__teks d-block mt-1" v-else-if="modeCetak === 'b4_8_kartu'">
                  8 kartu per lembar kertas B4 (grid 2 kolom &times; 4 baris).
                </small>
                <small class="kartu__teks d-block mt-1" v-else-if="modeCetak === 'b5_4_kartu'">
                  4 kartu per lembar kertas B5.
                </small>
                <small class="kartu__teks d-block mt-1" v-else>
                  Ukuran {{ lebarCetakMm }} &times; {{ tinggiCetakMm }} mm (cocok kertas foto / cetak satuan).
                </small>
              </label>
            </div>

            <div class="col-md-6" v-if="modeCetak === '1_per_halaman'">
              <label class="ui-field">
                <span class="ui-label">Ukuran Kartu</span>
                <select v-model.number="lebarCetakMm" class="ui-input">
                  <option v-for="u in ukuranCetak" :key="u.mm" :value="u.mm">
                    {{ u.nama }}
                  </option>
                </select>
              </label>
            </div>

            <div class="col-md-6">
              <div class="ui-field">
                <label class="ui-label">Filter Kontingen</label>
                <PilihCari
                  v-model="contingentId"
                  :options="kontingen"
                  placeholder="Semua kontingen"
                  cari-placeholder="Ketik nama kontingen…"
                  allow-empty
                  empty-label="Semua kontingen"
                />
              </div>
            </div>

            <div class="col-md-6">
              <label class="ui-field">
                <div class="d-flex justify-content-between align-items-center mb-1">
                  <span class="ui-label mb-0">Pangkas / Crop Bawah (Cegah Halaman Kosong)</span>
                  <span class="badge bg-light text-dark border">{{ potongBawahPersen }}%</span>
                </div>
                <input
                  type="range"
                  v-model.number="potongBawahPersen"
                  min="0"
                  max="25"
                  step="1"
                  class="form-range"
                />
                <small class="kartu__teks d-block mt-1">
                  Memotong sisa ruang bawah template agar tinggi kartu pas dan tidak memicu halaman cetak kosong.
                </small>
              </label>
            </div>
          </div>

          <div class="mt-3 d-flex flex-wrap gap-2 align-items-center">
            <button
              type="button"
              class="ui-btn ui-btn--primary ui-btn--lg"
              :disabled="memuatPdf || memuatData"
              @click="buatDanUnduhPDF"
            >
              <span v-if="memuatPdf" class="ui-spinner"></span>
              <i v-else class="bi bi-file-earmark-pdf-fill"></i>
              {{ memuatPdf ? 'Menyusun Berkas PDF…' : 'Cetak ID Card Massal / Unduh PDF' }}
            </button>

            <button
              type="button"
              class="ui-btn ui-btn--secondary ui-btn--lg"
              :disabled="memuatData || memuatPdf"
              @click="siapkanCetak"
            >
              <span v-if="memuatData" class="ui-spinner"></span>
              <i v-else class="bi bi-printer"></i>
              {{ memuatData ? 'Menyiapkan Data…' : 'Cetak Langsung (Printer Komputer)' }}
            </button>
          </div>

          <p class="kartu__teks text-muted small mt-2 mb-0">
            <i class="bi bi-info-circle text-primary me-1"></i>
            Dokumen PDF otomatis terbagi dalam lembar cetak A4 disertai garis potong untuk laminating kalung ID atlet, dan setiap kartu dilengkapi QR Code langsung ke jadwal pertandingan.
          </p>

          <!-- Bilah Kemajuan Pembuatan PDF -->
          <div v-if="memuatPdf" class="mt-3 p-3 bg-light rounded border">
            <div class="d-flex justify-content-between align-items-center mb-1 small fw-bold">
              <span><i class="bi bi-gear-wide-connected me-1"></i> {{ statusPdf }}</span>
              <span>{{ progressPdf }}%</span>
            </div>
            <div class="progress" style="height: 10px;">
              <div
                class="progress-bar progress-bar-striped progress-bar-animated bg-primary"
                role="progressbar"
                :style="{ width: `${progressPdf}%` }"
              ></div>
            </div>
            <small class="text-muted d-block mt-1">
              Dokumen PDF dibuat presisi sesuai ukuran kertas dan tata letak teks tanpa terpotong margin printer.
            </small>
          </div>

          <p v-if="jumlahCetak !== null" class="kartu__ok mt-3">
            <i class="bi bi-check-circle-fill"></i>
            <span>
              <strong>{{ jumlahCetak }} kartu</strong> siap dicetak.
            </span>
          </p>

          <ul v-if="rincianCetak.length" class="kartu__rincian">
            <li v-for="r in rincianCetak" :key="r.kategori">
              {{ r.kategori }} — <strong>{{ r.jumlah }}</strong> kartu
            </li>
          </ul>
        </div>
      </section>
    </template>

    <!-- Area Cetak Terisolasi (Teleport ke Body untuk Window.Print & html2canvas PDF) -->
    <Teleport to="body">
      <div id="area-cetak" class="area-cetak">
        <div
          v-for="(halaman, idxLembar) in lembarHalamanCetak"
          :key="idxLembar"
          class="lembar-cetak"
          :class="[`lembar--${modeCetak}`, { 'lembar--terakhir': idxLembar === lembarHalamanCetak.length - 1 }]"
          :style="gayaLembarCetak"
        >
          <div
            v-for="a in halaman"
            :key="a.id"
            class="kartu-cetak"
            :style="gayaKartuCetak"
          >
            <img :src="latarDataUrl || desain.latar_url" alt="" class="kartu-cetak__latar" crossorigin="anonymous" />
            <div
              v-for="k in (Array.isArray(kolom) ? kolom : [])"
              :key="k.kunci"
              class="kartu-cetak__kolom"
              :style="gayaKolom(k, true, a[k.kunci])"
            >
              <template v-if="k.kunci === 'foto'">
                <img v-if="a.foto_data_url || a.foto" :src="a.foto_data_url || a.foto" alt="" class="kartu-cetak__foto" crossorigin="anonymous" />
                <div v-else class="kartu-cetak__foto-kosong">
                  <i class="bi bi-person-fill"></i>
                </div>
              </template>
              <template v-else-if="k.kunci === 'qr_code'">
                <img v-if="qrMap[a.id]" :src="qrMap[a.id]" alt="QR Code" class="kartu-cetak__qr" />
                <div v-else class="kartu-cetak__qr-kosong">
                  <i class="bi bi-qr-code"></i>
                </div>
              </template>
              <template v-else>{{ a[k.kunci] || '' }}</template>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import http from '@/services/http';
import { useToast } from 'vue-toastification';
import PilihCari from '@/components/ui/PilihCari.vue';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import QRCode from 'qrcode';

const UKURAN_KERTAS_MM = {
  idcard_a4_9: { format: 'a4', orientasi: 'portrait', lebarMm: 210, tinggiMm: 297, perLembar: 9 },
  a4_4_kartu: { format: 'a4', orientasi: 'portrait', lebarMm: 210, tinggiMm: 297, perLembar: 4 },
  a4_8_kartu: { format: 'a4', orientasi: 'portrait', lebarMm: 210, tinggiMm: 297, perLembar: 8 },
  a4_2_kartu: { format: 'a4', orientasi: 'portrait', lebarMm: 210, tinggiMm: 297, perLembar: 2 },
  a5_2_kartu: { format: 'a5', orientasi: 'landscape', lebarMm: 210, tinggiMm: 148, perLembar: 2 },
  a5_1_kartu: { format: 'a5', orientasi: 'portrait', lebarMm: 148, tinggiMm: 210, perLembar: 1 },
  a6_1_kartu: { format: 'a6', orientasi: 'portrait', lebarMm: 105, tinggiMm: 148, perLembar: 1 },
  b4_8_kartu: { format: [250, 353], orientasi: 'portrait', lebarMm: 250, tinggiMm: 353, perLembar: 8 },
  b5_4_kartu: { format: [176, 250], orientasi: 'portrait', lebarMm: 176, tinggiMm: 250, perLembar: 4 },
  '1_per_halaman': { format: 'custom', orientasi: 'portrait', lebarMm: 105, tinggiMm: 148, perLembar: 1 },
};

const KOLOM_STANDAR = {
  nama: 'Nama Atlet',
  kontingen: 'Nama Kontingen',
  kategori: 'Kategori Pertandingan',
  kelompok_usia: 'Kelompok Usia',
  kelas: 'Kelas / Nomor Pertandingan',
  jenis_kelamin: 'Jenis Kelamin',
  tanggal_lahir: 'Tanggal Lahir',
  nomor: 'Nomor Peserta',
  foto: 'Pas Foto',
  qr_code: 'QR Code (Link Jadwal & BPJS)',
};

export default {
  name: 'IdCardPage',

  components: { PilihCari },

  setup() {
    return { toast: useToast() };
  },

  computed: {
    isPanitia() {
      try {
        const data = JSON.parse(localStorage.getItem('user') || '{}');
        const group = data.group?.name || '';
        return ['Owner', 'Event PIC', 'Admin'].includes(group);
      } catch (e) {
        return false;
      }
    },

    daftarPilihanKolom() {
      if (this.kolomTersedia && Object.keys(this.kolomTersedia).length > 0) {
        return this.kolomTersedia;
      }
      return KOLOM_STANDAR;
    },

    lembarHalamanCetak() {
      const cards = this.dataCetak || [];
      if (!cards.length) return [];

      let perLembar = 1;
      if (this.modeCetak === 'idcard_a4_9') perLembar = 9;
      else if (this.modeCetak === 'a4_4_kartu') perLembar = 4;
      else if (this.modeCetak === 'a4_8_kartu') perLembar = 8;
      else if (this.modeCetak === 'a4_2_kartu') perLembar = 2;
      else if (this.modeCetak === 'a5_2_kartu') perLembar = 2;
      else if (this.modeCetak === 'b4_8_kartu') perLembar = 8;
      else if (this.modeCetak === 'b5_4_kartu') perLembar = 4;

      const lembar = [];
      for (let i = 0; i < cards.length; i += perLembar) {
        lembar.push(cards.slice(i, i + perLembar));
      }
      return lembar;
    },

    gayaKartuCetak() {
      const l = Number(this.desain.lebar_asli) || 1000;
      const t = Number(this.desain.tinggi_asli) || 1500;
      const skalaTinggi = (100 - (this.potongBawahPersen || 0)) / 100;
      const tEfektif = t * skalaTinggi;

      if (this.modeCetak === 'idcard_a4_9') {
        // Ukuran fisik CR80 (KTP/ATM) dipaku, tidak ikut potongan bawah:
        // kartunya harus tetap pas di mika apa pun desainnya.
        return {
          width: '54mm',
          height: '85.6mm',
          overflow: 'hidden',
        };
      }
      if (this.modeCetak === 'a4_4_kartu') {
        const h = Math.round(138 * skalaTinggi);
        return {
          width: '94mm',
          height: `${h}mm`,
          overflow: 'hidden',
        };
      }
      if (this.modeCetak === 'a4_8_kartu') {
        const h = Math.round(68 * skalaTinggi);
        return {
          width: '94mm',
          height: `${h}mm`,
          overflow: 'hidden',
        };
      }
      if (this.modeCetak === 'a4_2_kartu') {
        const h = Math.round(190 * skalaTinggi);
        return {
          width: '130mm',
          height: `${h}mm`,
          overflow: 'hidden',
        };
      }
      if (this.modeCetak === 'a5_2_kartu') {
        const h = Math.round(138 * skalaTinggi);
        return {
          width: '95mm',
          height: `${h}mm`,
          overflow: 'hidden',
        };
      }
      if (this.modeCetak === 'a5_1_kartu') {
        const h = Math.round(195 * skalaTinggi);
        return {
          width: '135mm',
          height: `${h}mm`,
          overflow: 'hidden',
        };
      }
      if (this.modeCetak === 'a6_1_kartu') {
        const h = Math.round(148 * skalaTinggi);
        return {
          width: '105mm',
          height: `${h}mm`,
          overflow: 'hidden',
        };
      }
      if (this.modeCetak === 'b4_8_kartu') {
        const h = Math.round(155 * skalaTinggi);
        return {
          width: '105mm',
          height: `${h}mm`,
          overflow: 'hidden',
        };
      }
      if (this.modeCetak === 'b5_4_kartu') {
        const h = Math.round(118 * skalaTinggi);
        return {
          width: '80mm',
          height: `${h}mm`,
          overflow: 'hidden',
        };
      }

      const tCetak = Math.round((this.lebarCetakMm * tEfektif) / l);
      return {
        width: `${this.lebarCetakMm}mm`,
        height: `${tCetak}mm`,
        overflow: 'hidden',
      };
    },

    rincianCetak() {
      const hitung = {};

      for (const a of this.dataCetak) {
        const k = a.kategori || '-';
        hitung[k] = (hitung[k] || 0) + 1;
      }

      return Object.entries(hitung)
        .map(([kategori, jumlah]) => ({ kategori, jumlah }))
        .sort((a, b) => a.kategori.localeCompare(b.kategori));
    },

    tinggiCetakMm() {
      const l = Number(this.desain.lebar_asli) || 1000;
      const t = Number(this.desain.tinggi_asli) || 1500;
      const skalaTinggi = (100 - (this.potongBawahPersen || 0)) / 100;

      return Math.round((this.lebarCetakMm * t * skalaTinggi) / l);
    },

    gayaLembarCetak() {
      if (this.modeCetak === '1_per_halaman') {
        return {
          width: `${this.lebarCetakMm}mm`,
          height: `${this.tinggiCetakMm}mm`,
          maxWidth: `${this.lebarCetakMm}mm`,
          maxHeight: `${this.tinggiCetakMm}mm`,
        };
      }
      return {};
    },
  },

  data() {
    return {
      potongBawahPersen: 8,
      modeCetak: 'idcard_a4_9',
      lebarCetakMm: 105,
      ukuranCetak: [
        { mm: 54, nama: 'Kartu Kecil / KTP — 54 mm' },
        { mm: 74, nama: 'A7 — 74 mm' },
        { mm: 105, nama: 'A6 (Standar ID Card) — 105 mm' },
        { mm: 148, nama: 'A5 (ID Card Besar) — 148 mm' },
        { mm: 176, nama: 'B5 — 176 mm' },
        { mm: 210, nama: 'A4 — 210 mm' },
      ],
      turnamen: [],
      kontingen: [],
      tournamentId: '',
      contingentId: '',
      desain: { latar_url: null, lebar_asli: 0, tinggi_asli: 0 },
      kolomTersedia: { ...KOLOM_STANDAR },
      kolom: [],
      kolomBaru: '',
      aktif: null,
      menyimpan: false,
      mengunggah: false,
      menerapkanTemplate: false,
      galatUnggah: null,
      memuatData: false,
      memuatPdf: false,
      progressPdf: 0,
      statusPdf: '',
      latarDataUrl: null,
      dataCetak: [],
      daftarAtletPratinjau: [],
      selectedPreviewIndex: -1,
      jumlahCetak: null,
      seret: null,
      contoh: {
        nama: 'BUDI SANTOSO',
        kontingen: 'PSHT Cabang Banjarbaru',
        kategori: 'Tanding Prestasi',
        kelompok_usia: 'Remaja',
        kelas: 'Kelas A Putra - Remaja',
        jenis_kelamin: 'Putra',
        tanggal_lahir: '17/05/2009',
        nomor: '0001',
        foto: null,
      },
      contohQrUrl: null,
      qrMap: {},
    };
  },

  mounted() {
    document.title = 'Kartu Peserta (ID Card)';
    this.muatAwal();
  },

  methods: {
    async muatAwal() {
      try {
        const t = await http.get('/tournaments', { params: { fetch_all: true } });
        this.turnamen = this.isi(t);

        // Auto-pilih turnamen aktif agar tidak perlu pilih/upload ulang setiap buka halaman
        const queryTid = this.$route.query.tournament_id;
        const storedTid = localStorage.getItem('arspo_id_card_tournament_id');

        if (queryTid && this.turnamen.some((x) => String(x.id) === String(queryTid))) {
          this.tournamentId = queryTid;
        } else if (storedTid && this.turnamen.some((x) => String(x.id) === String(storedTid))) {
          this.tournamentId = storedTid;
        } else if (this.turnamen.length === 1) {
          this.tournamentId = this.turnamen[0].id;
        }

        if (this.tournamentId) {
          await this.muatDesain();
        }
      } catch (e) { /* handled by interceptor */ }
    },

    pilihAcara() {
      if (this.tournamentId) {
        localStorage.setItem('arspo_id_card_tournament_id', String(this.tournamentId));
      }
      this.muatDesain();
    },

    async muatKontingen() {
      const tid = this.tournamentId;
      try {
        const res = await http.get(`/contingents/by-tournament/${tid}`);
        if (String(tid) !== String(this.tournamentId)) return; // pilihan sudah berganti
        this.kontingen = this.isi(res);
        if (this.contingentId && !this.kontingen.some((k) => String(k.id) === String(this.contingentId))) {
          this.contingentId = '';
        }
      } catch (e) {
        this.kontingen = [];
      }
    },

    isi(res) {
      const d = res?.data;
      return Array.isArray(d) ? d : d?.data ?? [];
    },

    parseKolom(raw) {
      let val = raw;
      if (typeof val === 'string') {
        try { val = JSON.parse(val); } catch (e) { val = []; }
      }
      if (typeof val === 'string') {
        try { val = JSON.parse(val); } catch (e) { val = []; }
      }
      return Array.isArray(val) ? val : [];
    },

    async muatDesain() {
      this.kolom = [];
      this.aktif = null;
      this.dataCetak = [];
      this.daftarAtletPratinjau = [];
      this.selectedPreviewIndex = -1;
      this.jumlahCetak = null;
      this.desain = { latar_url: null, lebar_asli: 0, tinggi_asli: 0 };
      this.kontingen = [];
      if (!this.tournamentId) return;

      // Daftar kontingen hanya dari kejuaraan yang dipilih.
      this.muatKontingen();

      try {
        const [resDesain, resAtlet] = await Promise.all([
          http.get(`/id-card/${this.tournamentId}`),
          http.get(`/id-card/${this.tournamentId}/data-cetak`),
        ]);

        const kt = resDesain.data?.kolom_tersedia;
        this.kolomTersedia = (kt && Object.keys(kt).length > 0) ? kt : { ...KOLOM_STANDAR };
        if (resDesain.data?.desain) {
          this.desain = resDesain.data.desain;
          this.kolom = this.parseKolom(resDesain.data.desain.kolom);
        } else {
          this.kolom = [];
        }

        this.daftarAtletPratinjau = resAtlet.data?.atlet || [];
        this.muatContohQr();
      } catch (e) {
        this.kolomTersedia = { ...KOLOM_STANDAR };
      }
    },

    async muatContohQr(atletId = null) {
      try {
        const origin = window.location.origin;
        const targetId = atletId || (this.daftarAtletPratinjau[0]?.id) || 'demo';
        const link = `${origin}/kartu-atlet/${targetId}${this.tournamentId ? '?tournament_id=' + this.tournamentId : ''}`;
        this.contohQrUrl = await QRCode.toDataURL(link, {
          margin: 4,
          width: 256,
          errorCorrectionLevel: 'M',
        });
      } catch (e) {
        console.error('Gagal membuat pratinjau QR:', e);
      }
    },

    async gunakanTemplateBawaan() {
      if (!this.tournamentId) return;
      this.menerapkanTemplate = true;
      try {
        const { data } = await http.post(`/id-card/${this.tournamentId}/template-bawaan`);
        this.desain = data.desain;
        this.kolom = this.parseKolom(data.desain?.kolom);
        this.kolomTersedia = (data.kolom_tersedia && Object.keys(data.kolom_tersedia).length > 0)
          ? data.kolom_tersedia
          : { ...KOLOM_STANDAR };
        this.toast.success(data.message || 'Template bawaan berhasil diterapkan.');
        this.muatContohQr();
      } catch (err) {
        this.toast.error('Gagal menerapkan template bawaan.');
      } finally {
        this.menerapkanTemplate = false;
      }
    },

    aturUlangPosisiStandar() {
      this.kolom = [
        { kunci: 'foto', x: 50, y: 38, ukuran: 26, warna: '#000000', tebal: false, rata: 'center' },
        { kunci: 'nama', x: 50, y: 68, ukuran: 4.8, warna: '#111827', tebal: true, rata: 'center', lebar: 85 },
        { kunci: 'kontingen', x: 50, y: 74, ukuran: 4.0, warna: '#E84808', tebal: true, rata: 'center', lebar: 85 },
        { kunci: 'kelas', x: 50, y: 80, ukuran: 3.6, warna: '#111827', tebal: true, rata: 'center', lebar: 85 },
        { kunci: 'qr_code', x: 50, y: 86, ukuran: 11, warna: '#000000', tebal: false, rata: 'center' },
      ];
      this.toast.info('Posisi kolom diatur ke susunan standar ID card dengan QR Code.');
    },

    gantiPratinjauAtlet() {
      if (this.selectedPreviewIndex === -1 || !this.daftarAtletPratinjau[this.selectedPreviewIndex]) {
        this.contoh = {
          nama: 'BUDI SANTOSO',
          kontingen: 'PSHT Cabang Banjarbaru',
          kategori: 'Tanding Prestasi',
          kelompok_usia: 'Remaja',
          kelas: 'Kelas A Putra - Remaja',
          jenis_kelamin: 'Putra',
          tanggal_lahir: '17/05/2009',
          nomor: '0001',
          foto: null,
        };
        this.muatContohQr('demo');
      } else {
        const a = this.daftarAtletPratinjau[this.selectedPreviewIndex];
        this.contoh = {
          nama: a.nama,
          kontingen: a.kontingen,
          kategori: a.kategori,
          kelompok_usia: a.kelompok_usia,
          kelas: a.kelas,
          jenis_kelamin: a.jenis_kelamin || 'Putra',
          tanggal_lahir: a.tanggal_lahir,
          nomor: a.nomor,
          foto: a.foto,
        };
        this.muatContohQr(a.id);
      }
    },

    rataTengah(idx) {
      if (Array.isArray(this.kolom) && this.kolom[idx]) {
        this.kolom[idx].x = 50;
        this.kolom[idx].rata = 'center';
      }
    },

    setPosisi(idx, x, y, rata = 'center') {
      if (Array.isArray(this.kolom) && this.kolom[idx]) {
        this.kolom[idx].x = x;
        this.kolom[idx].y = y;
        if (rata) this.kolom[idx].rata = rata;
      }
    },

    async unggahLatar(e) {
      const berkas = e.target.files?.[0];
      if (!berkas) return;

      const fd = new FormData();
      fd.append('latar', berkas);

      this.mengunggah = true;
      this.galatUnggah = null;

      try {
        const { data } = await http.post(`/id-card/${this.tournamentId}/latar`, fd);
        this.desain = {
          latar_url: data.latar_url,
          lebar_asli: data.lebar_asli,
          tinggi_asli: data.tinggi_asli,
        };
        if (data.kolom_tersedia && Object.keys(data.kolom_tersedia).length > 0) {
          this.kolomTersedia = data.kolom_tersedia;
        } else if (!this.kolomTersedia || !Object.keys(this.kolomTersedia).length) {
          this.kolomTersedia = { ...KOLOM_STANDAR };
        }
        this.toast.success(data.message || 'Desain terpasang.');
      } catch (err) {
        this.galatUnggah = err?.friendlyMessage
          || err?.response?.data?.message
          || 'Desain gagal diunggah.';
      } finally {
        this.mengunggah = false;
        e.target.value = '';
      }
    },

    sudahDipakai(kunci) {
      if (!Array.isArray(this.kolom)) return false;
      return this.kolom.some((k) => k && k.kunci === kunci);
    },

    tambahKolom() {
      if (!Array.isArray(this.kolom)) this.kolom = [];
      if (!this.kolomBaru || this.sudahDipakai(this.kolomBaru)) return;
      const foto = this.kolomBaru === 'foto';
      const isQr = this.kolomBaru === 'qr_code';
      this.kolom.push({
        kunci: this.kolomBaru,
        x: 50,
        y: isQr ? 86 : Math.min(85, 20 + this.kolom.length * 8),
        ukuran: foto ? 28 : (isQr ? 12 : 4),
        warna: '#000000',
        tebal: false,
        rata: 'center',
        lebar: 80,
      });
      this.aktif = this.kolom.length - 1;
      this.kolomBaru = '';
    },

    hapusKolom(i) {
      if (Array.isArray(this.kolom)) {
        this.kolom.splice(i, 1);
      }
      this.aktif = null;
    },

    gayaKolom(k, isPrint = false, teksNilai = '') {
      if (!k) return {};
      const rata = k.rata || 'center';

      if (k.kunci === 'foto') {
        const ukuran = Number(k.ukuran) || 25;
        let leftPct = Number(k.x) || 50;
        if (rata === 'center') {
          leftPct = leftPct - (ukuran / 2);
        } else if (rata === 'right') {
          leftPct = leftPct - ukuran;
        }

        return {
          left: `${leftPct}%`,
          top: `${k.y}%`,
          width: `${ukuran}%`,
          aspectRatio: '3 / 4',
          transform: 'none',
        };
      }

      if (k.kunci === 'qr_code') {
        const ukuran = Number(k.ukuran) || 12;
        let leftPct = Number(k.x) || 50;
        if (rata === 'center') {
          leftPct = leftPct - (ukuran / 2);
        } else if (rata === 'right') {
          leftPct = leftPct - ukuran;
        }

        return {
          left: `${leftPct}%`,
          top: `${k.y}%`,
          width: `${ukuran}%`,
          aspectRatio: '1 / 1',
          transform: 'none',
        };
      }

      let baseWidthMm = this.lebarCetakMm || 105;
      if (this.modeCetak === 'a4_4_kartu') {
        baseWidthMm = 94;
      } else if (this.modeCetak === 'a4_8_kartu') {
        baseWidthMm = 94;
      } else if (this.modeCetak === 'idcard_a4_9') {
        baseWidthMm = 54;
      } else if (this.modeCetak === 'a4_2_kartu') {
        baseWidthMm = 130;
      } else if (this.modeCetak === 'a5_2_kartu') {
        baseWidthMm = 95;
      } else if (this.modeCetak === 'a5_1_kartu') {
        baseWidthMm = 135;
      } else if (this.modeCetak === 'a6_1_kartu') {
        baseWidthMm = 105;
      } else if (this.modeCetak === 'b4_8_kartu') {
        baseWidthMm = 105;
      } else if (this.modeCetak === 'b5_4_kartu') {
        baseWidthMm = 80;
      }

      let ukuranRelatif = Number(k.ukuran) || 4;

      // Smart auto-scale font jika teks panjang (> 18 huruf) agar muat lebih banyak huruf sebelum terkena elipsis (...)
      const str = String(teksNilai || '');
      const len = str.length;
      if (len > 30) {
        ukuranRelatif *= 0.72;
      } else if (len > 24) {
        ukuranRelatif *= 0.82;
      } else if (len > 18) {
        ukuranRelatif *= 0.90;
      }

      const fontSizeMm = (ukuranRelatif * baseWidthMm) / 100;
      const maxLebarNum = k.lebar ? Number(k.lebar) : 80;
      let leftPct = Number(k.x) || 50;
      if (rata === 'center') {
        leftPct = leftPct - (maxLebarNum / 2);
      } else if (rata === 'right') {
        leftPct = leftPct - maxLebarNum;
      }

      return {
        left: `${leftPct}%`,
        top: `${k.y}%`,
        fontSize: isPrint ? `${fontSizeMm}mm` : `${ukuranRelatif}cqw`,
        color: k.warna || '#111827',
        fontWeight: k.tebal ? '700' : '600',
        textAlign: rata,
        transform: 'none',
        maxWidth: `${maxLebarNum}%`,
        width: `${maxLebarNum}%`,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      };
    },

    lebarKolom(k) {
      if (k.lebar) return k.lebar;
      const x = Number(k.x) || 0;
      if (k.rata === 'right') return Math.max(10, x);
      if (k.rata === 'center') return Math.max(10, Math.min(x, 100 - x) * 2);
      return Math.max(10, 100 - x);
    },

    mulaiGeser(i, e) {
      this.aktif = i;
      const kotak = this.$refs.kanvas.getBoundingClientRect();
      this.seret = {
        i,
        dx: e.clientX - kotak.left - (this.kolom[i].x / 100) * kotak.width,
        dy: e.clientY - kotak.top - (this.kolom[i].y / 100) * kotak.height,
      };
      e.target.setPointerCapture?.(e.pointerId);
    },

    geser(e) {
      if (!this.seret) return;
      const kotak = this.$refs.kanvas.getBoundingClientRect();
      const k = this.kolom[this.seret.i];
      k.x = Math.round(((e.clientX - kotak.left - this.seret.dx) / kotak.width) * 1000) / 10;
      k.y = Math.round(((e.clientY - kotak.top - this.seret.dy) / kotak.height) * 1000) / 10;
      k.x = Math.min(105, Math.max(-5, k.x));
      k.y = Math.min(105, Math.max(-5, k.y));
    },

    lepas() {
      this.seret = null;
    },

    async simpanKolom() {
      this.menyimpan = true;
      try {
        await http.post(`/id-card/${this.tournamentId}/kolom`, { kolom: this.kolom });
        this.toast.success('Tata letak tersimpan.');
      } catch (e) { /* handled by interceptor */ }
      finally { this.menyimpan = false; }
    },

    async buatDanUnduhPDF() {
      if (this.memuatPdf || this.memuatData) return;
      this.memuatPdf = true;
      this.progressPdf = 5;
      this.statusPdf = 'Mengambil data atlet…';

      try {
        const { data } = await http.get(`/id-card/${this.tournamentId}/data-cetak`, {
          params: this.contingentId ? { contingent_id: this.contingentId } : {},
        });

        const atletList = data.atlet || [];
        this.jumlahCetak = data.jumlah;

        if (!atletList.length) {
          this.toast.error('Belum ada atlet terdaftar untuk dicetak.');
          this.memuatPdf = false;
          return;
        }

        this.progressPdf = 15;
        this.statusPdf = 'Memuat gambar latar kartu…';

        if (this.desain.latar_url) {
          this.latarDataUrl = await this.gambarKeDataUrl(this.desain.latar_url);
        }

        this.progressPdf = 25;
        this.statusPdf = 'Menyiapkan foto peserta…';

        const batchSize = 5;
        for (let b = 0; b < atletList.length; b += batchSize) {
          const chunk = atletList.slice(b, b + batchSize);
          await Promise.all(
            chunk.map(async (a) => {
              if (a.foto) {
                a.foto_data_url = await this.gambarKeDataUrl(a.foto);
              }
            })
          );
          const percent = 25 + Math.round(((b + chunk.length) / atletList.length) * 15);
          this.progressPdf = Math.min(40, percent);
        }

        const origin = window.location.origin;
        for (const a of atletList) {
          const link = a.qr_url || `${origin}/kartu-atlet/${a.id}${this.tournamentId ? '?tournament_id=' + this.tournamentId : ''}`;
          try {
            this.qrMap[a.id] = await QRCode.toDataURL(link, {
              // Tepi putih 4 modul = "quiet zone" standar QR. Tanpa tepi ini
              // pola hitam menempel ke latar kartu dan pemindai sering gagal.
              margin: 4,
              width: 256,
              errorCorrectionLevel: 'M',
            });
          } catch (err) {
            console.error('Gagal generate QR untuk PDF:', a.id, err);
          }
        }
        this.qrMap = { ...this.qrMap };

        this.dataCetak = atletList;

        this.progressPdf = 45;
        this.statusPdf = 'Menata lembar cetak…';
        await this.$nextTick();

        if (document.fonts && document.fonts.ready) {
          await document.fonts.ready;
        }

        await new Promise((resolve) => setTimeout(resolve, 300));

        const spec = UKURAN_KERTAS_MM[this.modeCetak] || UKURAN_KERTAS_MM.a4_4_kartu;
        let formatPdf = spec.format;
        let orientasi = spec.orientasi;
        let lebarMm = spec.lebarMm;
        let tinggiMm = spec.tinggiMm;

        if (this.modeCetak === '1_per_halaman') {
          lebarMm = this.lebarCetakMm;
          tinggiMm = this.tinggiCetakMm;
          formatPdf = [lebarMm, tinggiMm];
          orientasi = lebarMm > tinggiMm ? 'landscape' : 'portrait';
        }

        const pdf = new jsPDF({
          unit: 'mm',
          format: formatPdf,
          orientation: orientasi,
          compress: true,
        });

        const lembarElements = document.querySelectorAll('#area-cetak .lembar-cetak');
        const totalLembar = lembarElements.length;

        if (!totalLembar) {
          this.toast.error('Gagal menyiapkan lembar cetak.');
          this.memuatPdf = false;
          return;
        }

        for (let i = 0; i < totalLembar; i++) {
          const lembarEl = lembarElements[i];
          const startPct = 45;
          const endPct = 95;
          this.progressPdf = Math.round(startPct + ((i + 1) / totalLembar) * (endPct - startPct));
          this.statusPdf = `Menyusun PDF lembar ${i + 1} dari ${totalLembar} (${this.progressPdf}%)…`;

          const canvas = await html2canvas(lembarEl, {
            scale: 2,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: 0,
            windowWidth: lembarEl.scrollWidth || 1200,
            windowHeight: lembarEl.scrollHeight || 1600,
          });

          const imgData = canvas.toDataURL('image/jpeg', 0.92);

          if (i > 0) {
            pdf.addPage(formatPdf, orientasi);
          }

          pdf.addImage(imgData, 'JPEG', 0, 0, lebarMm, tinggiMm, undefined, 'FAST');
        }

        this.progressPdf = 100;
        this.statusPdf = 'Mengunduh berkas PDF…';

        const turnamenTerpilih = this.turnamen.find((t) => String(t.id) === String(this.tournamentId));
        const namaTurnamen = (turnamenTerpilih?.name || 'Kejuaraan').replace(/[^a-zA-Z0-9_-]/g, '_');
        const kontingenTerpilih = this.kontingen.find((k) => String(k.id) === String(this.contingentId));
        const namaKontingen = kontingenTerpilih ? `-${kontingenTerpilih.name.replace(/[^a-zA-Z0-9_-]/g, '_')}` : '';

        pdf.save(`ID-Card-${namaTurnamen}${namaKontingen}.pdf`);
        this.toast.success(`PDF ID Card (${this.dataCetak.length} atlet) berhasil diunduh!`);
      } catch (err) {
        console.error('Gagal membuat PDF ID Card:', err);
        this.toast.error('Terjadi kendala saat menyusun berkas PDF.');
      } finally {
        this.memuatPdf = false;
      }
    },

    async gambarKeDataUrl(url) {
      if (!url) return null;
      if (url.startsWith('data:')) return url;
      try {
        const res = await fetch(url, { mode: 'cors' });
        if (!res.ok) return url;
        const blob = await res.blob();
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.onerror = () => resolve(url);
          reader.readAsDataURL(blob);
        });
      } catch (e) {
        return url;
      }
    },

    async siapkanCetak() {
      this.memuatData = true;
      this.jumlahCetak = null;
      try {
        const { data } = await http.get(`/id-card/${this.tournamentId}/data-cetak`, {
          params: this.contingentId ? { contingent_id: this.contingentId } : {},
        });
        this.dataCetak = data.atlet || [];
        this.jumlahCetak = data.jumlah;

        if (!this.dataCetak.length) {
          this.toast.error('Belum ada atlet terdaftar untuk dicetak.');
          return;
        }

        const origin = window.location.origin;
        for (const a of this.dataCetak) {
          const link = a.qr_url || `${origin}/kartu-atlet/${a.id}${this.tournamentId ? '?tournament_id=' + this.tournamentId : ''}`;
          try {
            this.qrMap[a.id] = await QRCode.toDataURL(link, {
              // Tepi putih 4 modul = "quiet zone" standar QR. Tanpa tepi ini
              // pola hitam menempel ke latar kartu dan pemindai sering gagal.
              margin: 4,
              width: 256,
              errorCorrectionLevel: 'M',
            });
          } catch (err) {
            console.error('Gagal generate QR untuk cetak:', a.id, err);
          }
        }
        this.qrMap = { ...this.qrMap };

        // Injeksi ukuran @page secara dinamis agar browser (Chrome/Edge) memformat kertas sesuai pilihan
        let styleEl = document.getElementById('arspo-dynamic-page-size');
        if (!styleEl) {
          styleEl = document.createElement('style');
          styleEl.id = 'arspo-dynamic-page-size';
          document.head.appendChild(styleEl);
        }

        let pSize = 'A4 portrait';
        if (this.modeCetak === 'idcard_a4_9' || this.modeCetak === 'a4_4_kartu' || this.modeCetak === 'a4_2_kartu') {
          pSize = 'A4 portrait';
        } else if (this.modeCetak === 'a5_2_kartu') {
          pSize = 'A5 landscape';
        } else if (this.modeCetak === 'a5_1_kartu') {
          pSize = 'A5 portrait';
        } else if (this.modeCetak === 'a6_1_kartu') {
          pSize = '105mm 148mm';
        } else if (this.modeCetak === 'b4_8_kartu') {
          pSize = 'B4 portrait';
        } else if (this.modeCetak === 'b5_4_kartu') {
          pSize = 'B5 portrait';
        } else if (this.modeCetak === '1_per_halaman') {
          pSize = `${this.lebarCetakMm}mm ${this.tinggiCetakMm}mm`;
        }

        styleEl.innerHTML = `@media print { @page { size: ${pSize} !important; margin: 0mm !important; } }`;

        await this.$nextTick();
        setTimeout(() => window.print(), 500);
      } catch (e) {
        this.toast.error('Gagal memuat data atlet untuk dicetak.');
      } finally {
        this.memuatData = false;
      }
    },
  },
};
</script>

<style scoped>
.kartu__blok { margin-bottom: var(--space-5); }

.desain-status-card {
  padding: var(--space-4);
  background: rgba(16, 185, 129, 0.08);
  border: 1px solid rgba(16, 185, 129, 0.25);
  border-radius: var(--radius-md);
}

.desain-thumb {
  width: 52px;
  height: 78px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--border-subtle);
  background: #fff;
}

.desain-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.desain-meta { font-size: 0.82rem; color: var(--text-secondary); }
.desain-ket { font-size: 0.85rem; line-height: 1.5; color: var(--text-secondary); }

.kartu__judul {
  display: flex; align-items: center; gap: var(--space-3);
  margin: 0 0 var(--space-3); font-size: var(--text-lg);
}

.kartu__nomor {
  display: grid; place-items: center;
  width: 28px; height: 28px; flex-shrink: 0;
  border-radius: 50%; background: var(--accent); color: #fff;
  font-size: var(--text-sm); font-weight: 700;
}

.kartu__teks { margin: 0 0 var(--space-4); font-size: var(--text-sm); color: var(--text-secondary); }
.kartu__rincian {
  margin: .5rem 0 0;
  padding-left: 1.25rem;
  font-size: .9rem;
  line-height: 1.7;
  color: var(--text-secondary, #6b7280);
}

.kartu__galat {
  display: flex;
  align-items: flex-start;
  gap: .5rem;
  margin-top: .75rem;
  padding: .65rem .8rem;
  border-radius: 8px;
  background: rgba(220, 38, 38, .1);
  color: #b91c1c;
  font-size: .9rem;
  line-height: 1.5;
}

.kartu__ok { margin: var(--space-3) 0 0; font-size: var(--text-sm); color: var(--success-500); }

.kanvas {
  position: relative;
  container-type: inline-size;
  width: 100%;
  max-width: 520px;
  margin-inline: auto;
  border: 1px solid var(--border-subtle);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-sm);
  overflow: hidden;
  touch-action: none;
  user-select: none;
  background: #faf8f6;
}

.kanvas__latar { width: 100%; height: 100%; object-fit: contain; display: block; }

.kolom {
  position: absolute;
  cursor: grab;
  line-height: 1.2;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  box-sizing: border-box;
  outline: 1px dashed rgba(0, 0, 0, 0.35);
}

.kolom--aktif { outline: 2px solid var(--accent); background: rgba(232, 72, 8, 0.12); }

.kolom--foto {
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  overflow: hidden;
  outline: 1px dashed var(--accent);
}

.kolom__foto-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
}

.kolom__foto-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: var(--text-muted);
  pointer-events: none;
  user-select: none;
}

.kolom__foto-label { font-size: 0.65rem; font-weight: 700; letter-spacing: 0.1em; color: var(--text-secondary); }

.kolom--qr {
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border-radius: 4px;
  overflow: hidden;
  outline: 1px dashed var(--accent);
  padding: 2px;
}

.kolom__qr-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  pointer-events: none;
  user-select: none;
  -webkit-user-drag: none;
}

.kolom__qr-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: var(--text-muted);
}

.kolom__qr-label {
  font-size: 0.55rem;
  font-weight: 700;
  letter-spacing: 0.05em;
  color: var(--text-secondary);
}

.kartu__setelan {
  margin-top: var(--space-4);
  padding: var(--space-4);
  background: var(--bg-sunken);
  border-radius: var(--radius-md);
  border: 1px solid var(--border-subtle);
}

.kartu__setelan-judul {
  display: block;
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--text-primary);
}

.kartu__setelan-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--space-3);
  font-size: var(--text-sm);
}

.kartu__setelan-grid label {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  font-weight: 600;
  color: var(--text-primary);
}

.kartu__setelan-grid input[type="range"] {
  width: 100%;
  accent-color: var(--accent);
  cursor: pointer;
  height: 28px;
}

.posisi-pill {
  min-height: 38px;
  border-radius: 8px;
  font-weight: 600;
  border: 1px solid var(--border-subtle);
  transition: all 0.15s ease;
}

.posisi-pill:hover {
  border-color: var(--accent);
  background-color: var(--bg-hover);
}
</style>

<style>
/* ── Area Cetak Terisolasi (Off-screen untuk HTML2Canvas PDF & Normal Print) ── */
.area-cetak {
  position: fixed !important;
  left: -99999px !important;
  top: 0 !important;
  display: block !important;
  visibility: visible !important;
  opacity: 1 !important;
  pointer-events: none !important;
  z-index: -999 !important;
  background: #ffffff !important;
  margin: 0 !important;
  padding: 0 !important;
}

/* ── Wadah Lembar Kertas Sesuai Dimensi Fisik (mm) ── */
.lembar-cetak {
  page-break-inside: avoid !important;
  break-inside: avoid !important;
  page-break-after: always !important;
  break-after: page !important;
  box-sizing: border-box !important;
  overflow: hidden !important;
  background: #ffffff !important;
  position: relative !important;
  margin: 0 auto !important;
}

.lembar-cetak.lembar--terakhir {
  page-break-after: avoid !important;
  break-after: avoid !important;
}

/* ── Dimensi Millimeter Tiap Jenis Kertas (Bukan 100vh) ── */
.lembar--idcard_a4_9,
.lembar--a4_4_kartu,
.lembar--a4_8_kartu,
.lembar--a4_2_kartu {
  width: 210mm !important;
  height: 297mm !important;
  max-width: 210mm !important;
  max-height: 297mm !important;
  min-height: 297mm !important;
}

.lembar--a5_2_kartu {
  width: 210mm !important;
  height: 148mm !important;
  max-width: 210mm !important;
  max-height: 148mm !important;
  min-height: 148mm !important;
}

.lembar--a5_1_kartu {
  width: 148mm !important;
  height: 210mm !important;
  max-width: 148mm !important;
  max-height: 210mm !important;
  min-height: 210mm !important;
}

.lembar--a6_1_kartu {
  width: 105mm !important;
  height: 148mm !important;
  max-width: 105mm !important;
  max-height: 148mm !important;
  min-height: 148mm !important;
}

.lembar--b4_8_kartu {
  width: 250mm !important;
  height: 353mm !important;
  max-width: 250mm !important;
  max-height: 353mm !important;
  min-height: 353mm !important;
}

.lembar--b5_4_kartu {
  width: 176mm !important;
  height: 250mm !important;
  max-width: 176mm !important;
  max-height: 250mm !important;
  min-height: 250mm !important;
}

/* ── Tata Letak Grid Tiap Mode ── */
.lembar--1_per_halaman,
.lembar--a6_1_kartu,
.lembar--a5_1_kartu {
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
}

.lembar--idcard_a4_9 {
  display: grid !important;
  grid-template-columns: repeat(3, 54mm) !important;
  grid-template-rows: repeat(3, 85.6mm) !important;
  column-gap: 4mm !important;
  row-gap: 4mm !important;
  justify-content: center !important;
  align-content: center !important;
}

.lembar--a4_4_kartu {
  display: grid !important;
  grid-template-columns: repeat(2, 94mm) !important;
  grid-template-rows: repeat(2, auto) !important;
  column-gap: 6mm !important;
  row-gap: 6mm !important;
  justify-content: center !important;
  align-content: center !important;
  padding: 8mm 0 !important;
}

.lembar--a4_8_kartu {
  display: grid !important;
  grid-template-columns: repeat(2, 94mm) !important;
  grid-template-rows: repeat(4, auto) !important;
  column-gap: 6mm !important;
  row-gap: 4mm !important;
  justify-content: center !important;
  align-content: center !important;
  padding: 6mm 0 !important;
}

.lembar--a4_2_kartu {
  display: flex !important;
  flex-direction: column !important;
  align-items: center !important;
  justify-content: center !important;
  gap: 8mm !important;
  padding: 8mm 0 !important;
}

.lembar--a5_2_kartu {
  display: grid !important;
  grid-template-columns: repeat(2, 95mm) !important;
  grid-template-rows: auto !important;
  gap: 5mm !important;
  justify-content: center !important;
  align-content: center !important;
  padding: 4mm !important;
}

.lembar--b4_8_kartu {
  display: grid !important;
  grid-template-columns: repeat(2, 105mm) !important;
  grid-template-rows: repeat(4, auto) !important;
  gap: 4mm !important;
  justify-content: center !important;
  align-content: center !important;
  padding: 4mm !important;
}

.lembar--b5_4_kartu {
  display: grid !important;
  grid-template-columns: repeat(2, 80mm) !important;
  grid-template-rows: repeat(2, auto) !important;
  gap: 4mm !important;
  justify-content: center !important;
  align-content: center !important;
  padding: 4mm !important;
}

/* ── Kartu Cetak ── */
.kartu-cetak {
  position: relative !important;
  overflow: hidden !important;
  box-sizing: border-box !important;
  background: #ffffff !important;
  border: 1px dashed rgba(0, 0, 0, 0.25) !important;
}

.lembar--1_per_halaman .kartu-cetak,
.lembar--a6_1_kartu .kartu-cetak {
  border: none !important;
}

.kartu-cetak__latar {
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  width: 100% !important;
  height: 100% !important;
  object-fit: fill !important;
}

.kartu-cetak__kolom {
  position: absolute !important;
  line-height: 1.2 !important;
  white-space: nowrap !important;
  overflow: hidden !important;
  text-overflow: ellipsis !important;
  box-sizing: border-box !important;
}

.kartu-cetak__foto {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  border-radius: 4px !important;
}

.kartu-cetak__foto-kosong {
  width: 100% !important;
  height: 100% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: #e5e7eb !important;
  border: 1px solid #d1d5db !important;
  border-radius: 4px !important;
  font-size: 24pt !important;
  color: #9ca3af !important;
}

.kartu-cetak__qr {
  width: 100% !important;
  height: 100% !important;
  object-fit: contain !important;
  background: #ffffff !important;
}

.kartu-cetak__qr-kosong {
  width: 100% !important;
  height: 100% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  background: #f3f4f6 !important;
  border: 1px solid #d1d5db !important;
  border-radius: 2px !important;
  font-size: 16pt !important;
  color: #9ca3af !important;
}

@media print {
  #app { display: none !important; }
  @page {
    margin: 0mm !important;
    size: auto;
  }
  html, body {
    margin: 0 !important;
    padding: 0 !important;
    background: #fff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
    color-adjust: exact !important;
  }

  .area-cetak {
    position: static !important;
    left: 0 !important;
    top: 0 !important;
    display: block !important;
    z-index: auto !important;
  }
}
</style>